<template>
  <div class="checkbox-group" :class="computedClass">
    <BaseCheckbox
      v-for="(option, index) in props.options"
      :key="index"
      :model-value="modelValue.includes(option.value)"
      @update:model-value="handleChange(option)"
    >
      <BaseTypography type="text-secondary" weight="400">
        {{ option.label }}
      </BaseTypography>
    </BaseCheckbox>
  </div>
</template>

<script lang="ts" setup>
import { useAnimationShake } from '../../lib'
import BaseTypography from '../typography/index.vue'
import BaseCheckbox from './index.vue'

interface Option {
  label: string
  value: string
}
interface Props {
  modelValue?: string[] | null
  options: Option[]
  direction?: 'row' | 'column'
  form?: 'default' | 'input'
  variant?: 'default' | 'light'
  errorMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  direction: 'row',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

const modelValue = computed(() => props.modelValue ?? [])

const { shakeClass } = useAnimationShake(() => props.errorMessage)

const computedClass = computed(() => {
  return {
    [`checkbox-group--direction-${props.direction}`]: true,
    [`checkbox-group--form-${props.form}`]: props.form,
    [`checkbox-group--variant-${props.variant}`]: props.variant,
    'checkbox-group--error': props.errorMessage,
    [shakeClass.value]: true,
  }
})

function handleChange(option: Option) {
  const isChecked = modelValue.value.includes(option.value)

  let newValue: string[]

  if (isChecked) {
    newValue = modelValue.value.filter(v => v !== option.value)
  }
  else {
    newValue = [...modelValue.value, option.value]
  }

  emit('update:modelValue', newValue)
}
</script>

<style lang="scss" scoped>
.checkbox-group {
  display: flex;
  gap: rem(12px);

  &--direction {
    &-row {
      flex-direction: row;
    }

    &-column {
      flex-direction: column;
    }
  }

  &--form {
    &-input {
      &:deep(.base-checkbox) {
        --checkbox-size: 20px;

        border-radius: rem(12px);
        min-height: 38px;
        padding: 0 rem(9px);
        display: flex;
        align-items: center;

        p {
          padding: rem(12px) 0;
        }

        > * {
          flex: 1;
        }
      }

      &:deep(.base-checkbox__wrapper) {
        align-self: center;
      }
    }
  }

  &--form-input.checkbox-group--variant {
    &-light {
      &:deep(.base-checkbox) {
        background-color: var(--color-surface-bg);
      }
    }
  }

  &--error:deep(.base-checkbox) {
    border: 2px solid var(--color-surface-danger);
  }
}
</style>
